import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { track } from '../lib/analytics'
import { api, ApiError, post, promoUntil } from '../lib/api'
import type { Promo } from '../lib/api'
import { useSession } from '../lib/session'
import { Button, Card, EmptyState, Skeleton, useToast } from '../ui'
import { Page } from './Page'
import styles from './billing.module.css'

export interface PlanPrice { base_paise: number; total_paise: number; razorpay_plan_id: string | null }
export interface PlanInfo { code: 'free' | 'pro' | 'trader'; name: string; trial_days: number; gst_rate: number; month: PlanPrice; year: PlanPrice }

export const PLAN_FEATURES: Record<PlanInfo['code'], string[]> = {
  free: ['Filings from NSE and BSE, 15 minutes delayed', '1 watchlist of 5 stocks', '08:45 pre-open digest and Saturday recap', 'Holdings import, corporate actions calendar, stock pages, Risk Radar'],
  pro: ['Filings the moment they land', 'Instant Telegram and email alerts', '5 watchlists of 50 stocks · 25 alert rules', 'Alerts on your holdings and watchlists', 'CSV export'],
  trader: ['Everything in Pro', '5 watchlists of 200 stocks', '100 alert rules', 'Priority on new features'],
}
export const rupees = (p: number) => `₹${(p / 100).toLocaleString('en-IN', { maximumFractionDigits: p % 100 ? 2 : 0 })}`

/** Launch-offer banner; renders nothing when no offer is running. */
export function PromoBanner({ promo }: { promo?: Promo | null }) {
  if (!promo?.active) return null
  const until = promoUntil(promo)
  return <p className={styles.promo} role="note"><strong>Launch offer: every plan is free for a limited time.</strong> No card, no payment — every account gets everything in Trader.{until ? ` Free until ${until}.` : ''}</p>
}

/** Plan cards with a monthly/yearly switch. `onChoose` handles the paid CTA. */
export function PlanCards({ plans, period, current, busy, onChoose, ctaFor, promo, signedIn }: {
  plans: PlanInfo[]; period: 'month' | 'year'; current?: string; busy?: string | null
  onChoose: (p: PlanInfo) => void; ctaFor: (p: PlanInfo) => string; promo?: Promo | null; signedIn?: boolean
}) {
  const offer = !!promo?.active
  return (
    <div className={styles.cards}>
      {plans.map(p => {
        const price = p[period]
        const isCurrent = current === p.code
        return (
          <section key={p.code} className={`${styles.planCard} ${p.code === 'pro' ? styles.featured : ''}`} aria-labelledby={`plan-${p.code}`}>
            {p.code === 'pro' && <span className={styles.ribbon}>Most popular</span>}
            <h2 id={`plan-${p.code}`} className={styles.planName}>{p.name}</h2>
            <p className={styles.price}>
              {p.code === 'free' ? <strong>₹0</strong>
                : offer ? <><s className={styles.was}>{rupees(price.base_paise)}</s><strong>₹0</strong><span>/{period === 'month' ? 'month' : 'year'}</span></>
                : <><strong>{rupees(price.base_paise)}</strong><span>/{period === 'month' ? 'month' : 'year'}</span></>}
            </p>
            {p.code !== 'free' && offer ? <p className={styles.freeNow}>Free during launch</p>
              : <p className={styles.gst}>{p.code === 'free' ? 'Free for ever' : `+ 18% GST (${rupees(price.total_paise)} total)${period === 'year' ? ` · ${rupees(Math.round(price.base_paise / 12))}/month` : ''}`}</p>}
            <ul className={styles.features}>{PLAN_FEATURES[p.code].map(f => <li key={f}>{f}</li>)}</ul>
            {offer ? (p.code === 'free' ? <p className={styles.gst} />
                : signedIn ? <p className={styles.included}>✓ Included in your account</p>
                : <Link className={styles.ctaLink} to="/login?signup=1&next=%2Fwatchlist">Get it free</Link>)
              : p.code === 'free' ? <p className={styles.gst}>{isCurrent ? 'Your current plan' : ''}</p>
              : <Button variant={p.code === 'pro' ? 'primary' : 'secondary'} disabled={isCurrent || !!busy} loading={busy === p.code} onClick={() => onChoose(p)}>{isCurrent ? 'Current plan' : ctaFor(p)}</Button>}
            {!offer && p.code !== 'free' && p.trial_days > 0 && <p className={styles.gst}>{p.trial_days}-day free trial · cancel any time</p>}
          </section>
        )
      })}
    </div>
  )
}

export function PricingPage() {
  const toast = useToast()
  const { me } = useSession()
  const [plans, setPlans] = useState<PlanInfo[] | null>(null)
  const [promo, setPromo] = useState<Promo | null>(null)
  const [billing, setBilling] = useState<{ subscription: { plan_code: string; period: string; provider: string | null } | null; trial_available: boolean } | null>(null)
  const [params] = useSearchParams()
  const wanted = params.get('plan')
  const [period, setPeriod] = useState<'month' | 'year'>(params.get('period') === 'year' ? 'year' : 'month')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api<{ data: PlanInfo[]; promo?: Promo }>('/billing/plans').then(r => { setPlans(r.data); setPromo(r.promo ?? null) }).catch(e => setError((e as Error).message))
    if (me) api<NonNullable<typeof billing>>('/billing').then(setBilling).catch(() => undefined)
  }, [me])

  const paid = billing?.subscription && billing.subscription.plan_code !== 'free' ? billing.subscription : null
  const choose = async (p: PlanInfo) => {
    setBusy(p.code)
    try {
      if (paid) {
        const r = await post<{ scheduled: string }>('/billing/change', { plan: p.code, period })
        toast.show({ tone: 'success', message: r.scheduled === 'now' ? 'Plan change requested — it applies as soon as Razorpay confirms.' : 'Your plan will change at the end of the current billing period.' })
        setBusy(null)
      } else {
        track('checkout-start', { plan: p.code, period })
        const r = await post<{ url: string }>('/billing/checkout', { plan: p.code, period })
        track('checkout-redirect', { plan: p.code, period })
        window.location.assign(r.url)
      }
    } catch (e) {
      if (e instanceof ApiError && e.code === 'billing_not_configured') track('checkout-unavailable', { plan: p.code })
      const msg = e instanceof ApiError && (e.code === 'billing_not_configured' || e.code === 'plans_not_synced') ? 'Payments are being set up. Please try again soon.' : e instanceof Error ? e.message : 'Something went wrong'
      toast.show({ tone: 'error', message: msg })
      setBusy(null)
    }
  }
  const ctaFor = (p: PlanInfo) => paid ? `Switch to ${p.name}` : billing?.trial_available !== false && p.trial_days > 0 ? `Start ${p.trial_days}-day free trial` : `Subscribe to ${p.name}`

  return (
    <Page title="Plans" subtitle={promo?.active ? 'All plans are free during our launch offer.' : 'Prices in rupees, excluding 18% GST. Pay by card or UPI Autopay; cancel any time.'}>
      <PromoBanner promo={promo} />
      {!promo?.active && wanted && (wanted === 'pro' || wanted === 'trader') && !paid && <p className={styles.note} role="status">You chose <strong>{wanted === 'pro' ? 'Pro' : 'Trader'}</strong> — start your trial below.</p>}
      <div className={styles.toggleRow}>
        <div role="group" aria-label="Billing period" className={styles.seg}>
          {(['month', 'year'] as const).map(p => <button key={p} type="button" aria-pressed={period === p} className={period === p ? styles.segOn : styles.segBtn} onClick={() => setPeriod(p)}>{p === 'month' ? 'Monthly' : 'Yearly · save up to 30%'}</button>)}
        </div>
      </div>
      {error ? <Card variant="flush"><EmptyState kind="error" title="Could not load plans" compact>{error}</EmptyState></Card>
        : !plans ? <Card><Skeleton lines={5} label="Loading plans" /></Card>
        : <PlanCards plans={plans} period={period} current={paid?.plan_code ?? (me ? 'free' : undefined)} busy={busy} onChoose={choose} ctaFor={ctaFor} promo={promo} signedIn={!!me} />}
      {promo?.active ? <p className={styles.note}>Paid plans start only after the offer ends. {me && <Link to="/billing">Billing</Link>}</p> : <p className={styles.note}>You'll finish payment on Razorpay's secure page. Trials start after you authorise a mandate (card or UPI Autopay); the first charge is on day {plans?.[1]?.trial_days ?? 7} unless you cancel. {me && <Link to="/billing">Billing and invoices</Link>}</p>}
    </Page>
  )
}
